// src/components/programs/ProgramForm/helpers/formValidator.js

import { isValidDateRange } from "../../../../utils/programs/dateCalculations";
import { getValidationMessages } from "./formValidationMessages";
import { HOTEL_PRICING_MODES } from "./formConstants";

/**
 * Validate specific step
 * Steps: 0=ProgramInfo, 1=Hotels, 2=Itinerary, 3=Quotation
 */
export const validateStep = (step, formData, isEdit, isArabic, activeLang = null) => {
    const errors = {};
    const messages = getValidationMessages(isArabic);

    if (step === 0) {
        // Program Info
        const titleAr = formData.title?.ar?.trim();
        const titleEn = formData.title?.en?.trim();

        if (!titleAr && !titleEn) {
            const key = activeLang === 'ar' ? 'title_ar' : 'title_en';
            errors[key] = activeLang === 'ar' ? messages.title_ar_required : messages.title_en_required;
        } else {
            if (activeLang === 'ar' && !titleAr) {
                errors.title_ar = messages.title_ar_required;
            }
            if (activeLang === 'en' && !titleEn) {
                errors.title_en = messages.title_en_required;
            }
        }

        const descAr = formData.description?.ar?.trim();
        const descEn = formData.description?.en?.trim();

        if (!descAr && !descEn) {
            const key = activeLang === 'ar' ? 'description_ar' : 'description_en';
            errors[key] = activeLang === 'ar' ? messages.description_ar_required : messages.description_en_required;
        } else {
            if (activeLang === 'ar' && !descAr) {
                errors.description_ar = messages.description_ar_required;
            }
            if (activeLang === 'en' && !descEn) {
                errors.description_en = messages.description_en_required;
            }
        }

        if (!formData.agent_id) {
            errors.agent_id = messages.agent_id_required;
        }

        if (!formData.city_ids || !Array.isArray(formData.city_ids) || formData.city_ids.length === 0) {
            errors.city_ids = messages.city_ids_required;
        }

        if (!formData.start_date) {
            errors.start_date = messages.start_date_required;
        }

        if (!formData.end_date) {
            errors.end_date = messages.end_date_required;
        }

        if (formData.start_date && formData.end_date) {
            if (!isValidDateRange(formData.start_date, formData.end_date)) {
                errors.end_date = messages.end_date_invalid;
            }
        }

        const totalPax = formData.pax_types?.reduce((sum, pt) => sum + (pt.count || 0), 0);
        if (!totalPax || totalPax === 0) {
            errors.pax_types = messages.pax_types_required;
        }

    } else if (step === 1) {
        // Hotels: validate formData.hotels[]
        const hotels = formData.hotels || [];

        // Hotels are optional — skip validation if no hotels
        if (hotels.length === 0) return errors;

        hotels.forEach((hotel, index) => {
            const prefix = `hotel_${hotel.id}`;
            // A hotel is room_night_daily (requires at least one room_stay) when it has stays,
            // was created/migrated with the room_night_daily pricing unit, or has no legacy
            // room_category_id. Legacy hotels (migrated per_person_night) keep the array empty
            // AND carry a room_category_id, so they must NOT fall into the room_stays branch.
            const isRoomNightDaily = Array.isArray(hotel.room_stays) && (
                hotel.room_stays.length > 0 ||
                hotel.product_snapshot?.pricing_unit === HOTEL_PRICING_MODES.ROOM_NIGHT_DAILY ||
                !hotel.room_category_id
            );

            // Check-in and out dates
            if (!hotel.check_in_date) {
                errors[`${prefix}_check_in_date`] = isArabic ? 'تاريخ الدخول مطلوب' : 'Check-in date is required';
            }
            if (!hotel.check_out_date) {
                errors[`${prefix}_check_out_date`] = isArabic ? 'تاريخ الخروج مطلوب' : 'Check-out date is required';
            }

            if (hotel.check_in_date && hotel.check_out_date) {
                const d1 = new Date(hotel.check_in_date);
                const d2 = new Date(hotel.check_out_date);
                if (d2 <= d1) {
                    errors[`${prefix}_check_out_date`] = isArabic
                        ? 'تاريخ الخروج يجب أن يكون بعد تاريخ الدخول'
                        : 'Check-out date must be after check-in date';
                }

                // Validate against program date range
                if (formData.start_date && hotel.check_in_date < formData.start_date) {
                    errors[`${prefix}_check_in_date`] = isArabic
                        ? 'تاريخ الدخول لا يمكن أن يكون قبل تاريخ بداية البرنامج'
                        : 'Check-in date cannot be before program start date';
                }
                if (formData.end_date && hotel.check_out_date > formData.end_date) {
                    errors[`${prefix}_check_out_date`] = isArabic
                        ? 'تاريخ الخروج لا يمكن أن يكون بعد تاريخ نهاية البرنامج'
                        : 'Check-out date cannot be after program end date';
                }
            }

            // Sequential overlap check: each stay must start after or on the previous stay's check-out
            if (index > 0) {
                const prev = hotels[index - 1];
                if (hotel.check_in_date && prev.check_out_date && hotel.check_in_date < prev.check_out_date) {
                    errors[`${prefix}_check_in_date`] = isArabic
                        ? 'تاريخ الدخول يتداخل مع الإقامة السابقة. يجب أن يكون بعد أو يساوي تاريخ خروج الإقامة السابقة'
                        : 'Check-in date overlaps with previous stay. Must be on or after previous stay\'s check-out date';
                }
            }

            // Common required fields
            if (!hotel.hotel_name?.trim()) {
                errors[`${prefix}_hotel_name`] = messages.hotel_name_required;
            }
            if (!hotel.country_id) {
                errors[`${prefix}_country_id`] = messages.hotel_country_required;
            }
            // city_id is optional — no validation required
            if (!hotel.check_in_time?.trim()) {
                errors[`${prefix}_check_in_time`] = messages.check_in_time_required;
            }
            if (!hotel.check_out_time?.trim()) {
                errors[`${prefix}_check_out_time`] = messages.check_out_time_required;
            }
            if (!hotel.meal_plan_id) {
                errors[`${prefix}_meal_plan_id`] = messages.meal_plan_required;
            }
            if (!hotel.hotel_rating_id) {
                errors[`${prefix}_hotel_rating_id`] = messages.hotel_rating_required;
            }

            if (isRoomNightDaily) {
                // NEW format: validate room_stays
                if (hotel.room_stays.length === 0) {
                    errors[`${prefix}_room_stays`] = isArabic ? 'يجب إضافة إقامة غرفة واحدة على الأقل' : 'At least one room stay is required';
                } else {
                    hotel.room_stays.forEach((rs, rsIndex) => {
                        if (!rs.date) {
                            errors[`${prefix}_rs_${rsIndex}_date`] = isArabic ? 'التاريخ مطلوب' : 'Date is required';
                        }
                        if (!rs.room_category_id) {
                            errors[`${prefix}_rs_${rsIndex}_room_category`] = isArabic ? 'فئة الغرفة مطلوبة' : 'Room category is required';
                        }
                        if (!rs.rooms_count || rs.rooms_count < 1) {
                            errors[`${prefix}_rs_${rsIndex}_rooms_count`] = isArabic ? 'عدد الغرف مطلوب' : 'Rooms count is required';
                        }
                        
                        if (rs.extras && Array.isArray(rs.extras)) {
                            rs.extras.forEach((extra, extraIdx) => {
                                if (!extra.quantity || extra.quantity < 1) {
                                    errors[`${prefix}_rs_${rsIndex}_extra_${extraIdx}_quantity`] = isArabic ? 'الكمية مطلوبة' : 'Quantity is required';
                                }
                            });
                        }
                    });

                    // Coverage validation: every hotel night must be covered by at least one room_stay
                    if (hotel.check_in_date && hotel.check_out_date) {
                        const expectedDates = [];
                        const current = new Date(hotel.check_in_date);
                        const end = new Date(hotel.check_out_date);
                        while (current < end) {
                            expectedDates.push(current.toISOString().split('T')[0]);
                            current.setDate(current.getDate() + 1);
                        }
                        const actualDates = [...new Set(hotel.room_stays.map(rs => rs.date))].sort();
                        const missingDates = expectedDates.filter(d => !actualDates.includes(d));
                        if (missingDates.length > 0) {
                            errors[`${prefix}_room_coverage`] = isArabic
                                ? `التواريخ التالية غير مغطاة: ${missingDates.join(', ')}`
                                : `Missing room coverage for: ${missingDates.join(', ')}`;
                        }
                        // Check no room_stay date is outside hotel range
                        const outsideDates = hotel.room_stays.filter(rs => rs.date && !expectedDates.includes(rs.date));
                        if (outsideDates.length > 0) {
                            errors[`${prefix}_room_outside`] = isArabic
                                ? `بعض تواريخ الغرف خارج نطاق الفندق`
                                : 'Some room dates are outside hotel date range';
                        }
                    }
                }
            } else {
                // LEGACY format: validate rooms + room_category_id
                if (!hotel.rooms || hotel.rooms < 1) {
                    errors[`${prefix}_rooms`] = messages.hotel_units_required;
                }
                if (!hotel.room_category_id) {
                    errors[`${prefix}_room_category_id`] = messages.room_category_required;
                }
            }
        });

    } else if (step === 2) {
        // Itinerary
        const hasProducts = formData.itinerary?.some((day) => day.products?.length > 0);
        if (!hasProducts) {
            errors.itinerary = messages.itinerary_required;
        }

        formData.itinerary?.forEach((day, dayIndex) => {
            if (day.products?.length > 0) {
                if (!day.day_name?.ar?.trim()) {
                    errors[`day_${dayIndex}_name_ar`] = messages.day_name_ar_required;
                }

                if (!day.day_name?.en?.trim()) {
                    errors[`day_${dayIndex}_name_en`] = messages.day_name_en_required;
                }

                day.products.forEach((product) => {
                    const pricingCode = product.product_snapshot?.pricing_unit;
                    const productTypeCode = product.product_snapshot?.productType?.code || product.product_snapshot?.productType ||
                        product.product_snapshot?.product_type?.code || product.product_snapshot?.product_type ||
                        product.productType?.code || product.productType ||
                        product.product_type?.code || product.product_type;
                    const isFreeDay = productTypeCode === 'free_day' || productTypeCode === 'free_time';
                    const isFlight = productTypeCode === 'flight';
                    const instanceId = product.instance_id;

                    if (isFreeDay) return;

                    if (isFlight) {
                        const flightDetails = product.dynamic_fields?.flight_details || {};
                        if (!flightDetails.airline_id) {
                            errors[`${instanceId}_airline_id`] = messages.airline_required;
                        }
                        if (!flightDetails.departure_airport_id) {
                            errors[`${instanceId}_departure_airport_id`] = messages.departure_airport_required;
                        }
                        if (!flightDetails.arrival_airport_id) {
                            errors[`${instanceId}_arrival_airport_id`] = messages.arrival_airport_required;
                        }
                        if (!flightDetails.departure_date) {
                            errors[`${instanceId}_departure_date`] = messages.departure_date_required;
                        }
                        if (!flightDetails.arrival_date) {
                            errors[`${instanceId}_arrival_date`] = messages.arrival_date_required;
                        }
                        if (!flightDetails.departure_time) {
                            errors[`${instanceId}_departure_time`] = messages.departure_time_required;
                        }
                        if (!flightDetails.arrival_time) {
                            errors[`${instanceId}_arrival_time`] = messages.arrival_time_required;
                        }
                        if (!flightDetails.flight_number) {
                            errors[`${instanceId}_flight_number`] = messages.flight_number_required;
                        }
                        if (!flightDetails.pnr) {
                            errors[`${instanceId}_pnr`] = messages.pnr_required;
                        }
                        if (flightDetails.baggage_weight === null || flightDetails.baggage_weight === undefined || flightDetails.baggage_weight === "") {
                            errors[`${instanceId}_baggage_weight`] = messages.baggage_weight_required;
                        }
                        return;
                    }

                    // Validate based on pricing_unit code
                    if (pricingCode === "per_person") {
                        if (!product.dynamic_fields?.pickup_time) {
                            errors[`${instanceId}_pickup_time`] = messages.pickup_time_required;
                        }
                        if (!product.dynamic_fields?.city_id) {
                            errors[`${instanceId}_city`] = messages.city_required;
                        }
                    } else if (pricingCode === "per_night") {
                        const nights = product.dynamic_fields?.nights;
                        if (nights != null) {
                            if (nights < 0) {
                                errors[`${instanceId}_nights`] = messages.nights_required;
                            } else if (nights > formData.durations_nights) {
                                errors[`${instanceId}_nights`] = messages.nights_exceeded(formData.durations_nights);
                            }
                        }
                    } else if (pricingCode === "per_unit") {
                        if (!product.dynamic_fields?.time) {
                            errors[`${instanceId}_time`] = messages.time_required;
                        }
                        const units = product.dynamic_fields?.units;
                        if (!units || units < 1) {
                            errors[`${instanceId}_units`] = messages.units_required;
                        }
                    }
                });
            }
        });

    } else if (step === 3) {
        // Quotation
        formData.itinerary?.forEach((day) => {
            day.products?.forEach((product) => {
                const instanceId = product.instance_id;
                const effectivePrice = product.pricing?.custom_price || product.pricing?.price;

                if (product.pricing?.is_price_edited) {
                    if (product.pricing.custom_price === '') {
                        errors[`${instanceId}_custom_price`] = isArabic ? 'السعر مطلوب' : 'Price is required';
                    }
                    if (product.pricing.custom_cost_price === '') {
                        errors[`${instanceId}_custom_cost_price`] = isArabic ? 'التكلفة مطلوبة' : 'Cost is required';
                    }
                    if (product.pricing.custom_pax_prices) {
                        Object.entries(product.pricing.custom_pax_prices).forEach(([paxId, price]) => {
                            if (price === '') {
                                errors[`${instanceId}_pax_${paxId}_price`] = isArabic ? 'السعر مطلوب' : 'Price is required';
                            }
                        });
                    }
                    if (product.pricing.custom_pax_costs) {
                        Object.entries(product.pricing.custom_pax_costs).forEach(([paxId, cost]) => {
                            if (cost === '') {
                                errors[`${instanceId}_pax_${paxId}_cost`] = isArabic ? 'التكلفة مطلوبة' : 'Cost is required';
                            }
                        });
                    }
                }

                if (product.pricing?.discount_type === "amount") {
                    const discount = product.pricing?.discount_value;
                    if (discount && discount > effectivePrice) {
                        errors[`${instanceId}_discount`] = messages.discount_exceeded;
                    }
                } else if (product.pricing?.discount_type === "percent") {
                    const discount = product.pricing?.discount_value;
                    if (discount && (discount < 0 || discount > 100)) {
                        errors[`${instanceId}_discount`] = messages.discount_percent_invalid;
                    }
                }
            });
        });

        formData.hotels?.forEach((hotel, hotelIndex) => {
            if (hotel.pricing?.custom_room_prices) {
                Object.entries(hotel.pricing.custom_room_prices).forEach(([i, price]) => {
                    if (price === '') {
                        errors[`hotel_${hotelIndex}_room_${i}_price`] = isArabic ? 'السعر مطلوب' : 'Price is required';
                    }
                });
            }
            if (hotel.pricing?.custom_room_costs) {
                Object.entries(hotel.pricing.custom_room_costs).forEach(([i, cost]) => {
                    if (cost === '') {
                        errors[`hotel_${hotelIndex}_room_${i}_cost`] = isArabic ? 'التكلفة مطلوبة' : 'Cost is required';
                    }
                });
            }
            if (hotel.pricing?.custom_extra_prices) {
                Object.entries(hotel.pricing.custom_extra_prices).forEach(([i, extra]) => {
                    if (extra && extra.sell_price === '') {
                        errors[`hotel_${hotelIndex}_extra_${i}_sell`] = isArabic ? 'السعر مطلوب' : 'Price is required';
                    }
                    if (extra && extra.cost_price === '') {
                        errors[`hotel_${hotelIndex}_extra_${i}_cost`] = isArabic ? 'التكلفة مطلوبة' : 'Cost is required';
                    }
                });
            }
        });

        if (formData.global_discount?.type === "percent") {
            const value = formData.global_discount?.value;
            if (value && (value < 0 || value > 100)) {
                errors.global_discount = messages.global_discount_invalid;
            }
        }
    }

    return errors;
};
